import type { InfiniteData } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import type {
  SharepointFilePickerProps,
  SharepointPickerItem,
} from '@/components/integrations/SharepointFilePicker/types'

import { ensureDateStringIsUTC } from '@/helpers/date'

import type {
  SharePointChildrenResponse,
  SharePointDrive,
  SharePointDriveItem,
  SharePointDriveListResponse,
  SharePointSite,
} from '@/hooks/sharepoint/types'
import type { FetchSharepointSitesResponse } from '@/hooks/sharepoint/useSharepointSites'

export interface PortableSharepointPickerBreadcrumb {
  id: string
  name: string
  type: 'drive' | 'folder' | 'site'
}

export interface SitesWithAccess {
  accessLimited: boolean
  selectedSitesMode: boolean
  sites: SharePointSite[]
}

export interface DrivesWithAccess {
  accessLimited: boolean
  drives: SharePointDrive[]
}

export const EMPTY_INITIAL_ITEMS: SharepointPickerItem[] = []
export const EMPTY_DRIVE_ITEMS: SharePointDriveItem[] = []
export const EMPTY_SITES: SharePointSite[] = []
export const EMPTY_DRIVES: SharePointDrive[] = []
export const EMPTY_BREADCRUMBS: PortableSharepointPickerBreadcrumb[] = []
export const EMPTY_INITIAL_TAG_IDS: NonNullable<
  SharepointFilePickerProps['initialTagIds']
> = []

export const selectSitesWithAccess = (
  data: FetchSharepointSitesResponse,
): SitesWithAccess => ({
  accessLimited: data.access_limited ?? false,
  selectedSitesMode: data.selected_sites_mode ?? false,
  sites: data.data,
})

export const selectDrivesWithAccess = (
  data: SharePointDriveListResponse,
): DrivesWithAccess => ({
  accessLimited: data.access_limited ?? false,
  drives: data.data,
})

export const getSelectionKey = (item: SharepointPickerItem) =>
  `${item.drive_id}:${item.drive_item_id}`

export const selectSortedDriveItems = (
  data: InfiniteData<SharePointChildrenResponse>,
) => {
  return data.pages
    .flatMap((page) => page.items)
    .toSorted((left, right) => {
      if (left.is_folder && !right.is_folder) {
        return -1
      }

      if (!left.is_folder && right.is_folder) {
        return 1
      }

      return left.name.localeCompare(right.name)
    })
}

/**
 * Whether a file is one of the kinds the caller asked for.
 *
 * Folders are always true — they are a way through the tree rather than
 * something being picked, and a folder full of matches would otherwise be
 * unreachable. An empty `accept` means the caller takes anything.
 *
 * Only extensions are matched. `accept` also carries MIME types through to
 * Microsoft's own picker, and a list of those alone would grey out every row
 * here rather than none.
 */
export const isSharepointItemAccepted = (
  item: Pick<SharePointDriveItem, 'is_folder' | 'name'>,
  accept: readonly string[],
) => {
  if (item.is_folder) {
    return true
  }

  const extensions = accept.filter((entry) => entry.startsWith('.'))
  if (extensions.length === 0) {
    return true
  }

  const name = item.name.toLowerCase()
  return extensions.some((extension) => name.endsWith(extension.toLowerCase()))
}

export const sharepointDriveItemToPickerItem = (
  item: SharePointDriveItem,
  driveId: string,
): SharepointPickerItem => ({
  drive_id: driveId,
  drive_item_id: item.id,
  is_folder: item.is_folder,
  name: item.name,
})

export const getRelativeDate = (value: string | null | undefined) => {
  if (!value) {
    return null
  }

  try {
    return formatDistanceToNow(new Date(ensureDateStringIsUTC(value)), {
      addSuffix: true,
    })
  } catch {
    return null
  }
}
